/* eslint-disable react/require-default-props */
import React from 'react';
import proptypes from 'prop-types';

import { extend } from '@thd-nucleus/data-sources';
import { PriceDetails } from './subcomponents/price/PriceDetails';
import { isBundleUnavailable } from '../utils/product-unavailable-utils';
import { CustomSkeletonLine } from './subcomponents/CustomSkeletonLine';
import useGetPlaceholderHeight from '../hooks/useGetPlaceholderHeight';

const { shape, string, bool, arrayOf } = proptypes;

/**
 * Component to display the price of a product bundle.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.itemId - The unique identifier for the item.
 * @param {Object} props.product - The product bundle object.
 * @param {Object} props.pricingDetails - The pricing details object.
 * @param {boolean} [props.pricingDetails.loading] - Indicates if the pricing details are still loading.
 * @param {Object} [props.pricingDetails.pricing] - The pricing information for the product bundle.
 * @returns {JSX.Element} The rendered price component or a skeleton loader if loading.
 */
export const BundlePrice = ({ itemId, product, pricingDetails = null, products }) => {

  const { hidePrice } = product?.features || {};
  const { pricePlaceholderHeight } = useGetPlaceholderHeight(product);
  const isUnavailable = hidePrice && isBundleUnavailable({ products });

  if (isUnavailable) {
    return null;
  }

  if (pricingDetails?.loading) {
    return <CustomSkeletonLine height={pricePlaceholderHeight} />;
  }

  return (
    <div data-testid="bundle-price">
      <PriceDetails
        itemId={itemId}
        product={product}
        pricingDetails={pricingDetails}
      />
    </div>
  );
};

BundlePrice.propTypes = {
  itemId: string.isRequired,
  product: shape({
    features: shape({ priceUnitOfMeasure: string.isRequired })
  }).isRequired,
  pricingDetails: shape({
    pricing: shape({}),
    loading: bool
  }),
  products: arrayOf(
    shape({ })
  ).isRequired
};

BundlePrice.displayName = 'BundlePrice';

BundlePrice.dataModel = extend(PriceDetails);
